import { Injectable, signal, computed } from '@angular/core';
import { CaseEntity } from '../../domain/entities/case.entity';
import { CaseListItemEntity } from '../../domain/entities/case-list-item.entity';
import { CaseStatusSnapshotEntity } from '../../domain/entities/case-status-snapshot.entity';
import { CasePrioritySnapshotEntity } from '../../domain/entities/case-priority-snapshot.entity';
import { ChecklistStepEntity } from '../../domain/entities/checklist-step.entity';
import { CaseMessageEntity } from '../../domain/entities/case-message.entity';
import { WorkNoteEntity } from '../../domain/entities/work-note.entity';
import { CaseAttachmentEntity } from '../../domain/entities/case-attachment.entity';
import { LinkedCaseEntity } from '../../domain/entities/linked-case.entity';
import { EmployeeEntity } from '../../domain/entities/employee.entity';
import { ParentChildCasesEntity } from '../../domain/entities/parent-child-cases.entity';

/**
 * Cases state management using Angular signals.
 * Manages Case state including data and loading states.
 */
@Injectable({ providedIn: 'root' })
export class CasesState {
  // Base case state
  private readonly _cases = signal<CaseEntity[]>([]);
  private readonly _selectedCase = signal<CaseEntity | null>(null);
  private readonly _loadingCases = signal(false);
  private readonly _savingCases = signal(false);
  private readonly _errorCases = signal<string | null>(null);

  // Cases list state (summary data for dropdowns)
  private readonly _casesList = signal<CaseListItemEntity[]>([]);
  private readonly _casesListTotalElements = signal<number>(0);
  private readonly _loadingCasesList = signal(false);
  private readonly _errorCasesList = signal<string | null>(null);

  // Status snapshots state
  private readonly _statusSnapshots = signal<CaseStatusSnapshotEntity[]>([]);
  private readonly _loadingStatusSnapshots = signal(false);
  private readonly _errorStatusSnapshots = signal<string | null>(null);

  // Priority snapshots state
  private readonly _prioritySnapshots = signal<CasePrioritySnapshotEntity[]>([]);
  private readonly _loadingPrioritySnapshots = signal(false);
  private readonly _errorPrioritySnapshots = signal<string | null>(null);

  // Checklist steps state
  private readonly _checklistSteps = signal<ChecklistStepEntity[]>([]);
  private readonly _loadingChecklistSteps = signal(false);
  private readonly _updatingChecklistStep = signal(false);
  private readonly _errorChecklistSteps = signal<string | null>(null);

  // Messages state
  private readonly _messages = signal<CaseMessageEntity[]>([]);
  private readonly _loadingMessages = signal(false);
  private readonly _addingMessage = signal(false);
  private readonly _markingMessagesAsRead = signal(false);
  private readonly _errorMessages = signal<string | null>(null);

  // Work notes state
  private readonly _workNotes = signal<WorkNoteEntity[]>([]);
  private readonly _loadingWorkNotes = signal(false);
  private readonly _addingWorkNote = signal(false);
  private readonly _errorWorkNotes = signal<string | null>(null);

  // Attachments state
  private readonly _attachments = signal<CaseAttachmentEntity[]>([]);
  private readonly _loadingAttachments = signal(false);
  private readonly _uploadingAttachment = signal(false);
  private readonly _deletingAttachmentId = signal<string | null>(null);
  private readonly _errorAttachments = signal<string | null>(null);

  // Linked cases state
  private readonly _linkedCases = signal<LinkedCaseEntity[]>([]);
  private readonly _loadingLinkedCases = signal(false);
  private readonly _linkingCase = signal(false);
  private readonly _unlinkingCase = signal(false);
  private readonly _errorLinkedCases = signal<string | null>(null);

  // Employees state
  private readonly _employees = signal<EmployeeEntity[]>([]);
  private readonly _loadingEmployees = signal(false);
  private readonly _errorEmployees = signal<string | null>(null);

  // Closing case signals
  private readonly _closingCase = signal(false);
  private readonly _errorClosingCase = signal<string | null>(null);

  // Reopening case signals
  private readonly _reopeningCase = signal(false);
  private readonly _errorReopeningCase = signal<string | null>(null);

  // Involved employees state
  private readonly _involvedEmployees = signal<EmployeeEntity[]>([]);
  private readonly _loadingInvolvedEmployees = signal(false);
  private readonly _errorInvolvedEmployees = signal<string | null>(null);

  // Parent/child cases state
  private readonly _parentChildCases = signal<ParentChildCasesEntity | null>(null);
  private readonly _loadingParentChildCases = signal(false);
  private readonly _errorParentChildCases = signal<string | null>(null);

  // Read-only signals - Base case
  public readonly cases = this._cases.asReadonly();
  public readonly selectedCase = this._selectedCase.asReadonly();
  public readonly loadingCases = this._loadingCases.asReadonly();
  public readonly savingCases = this._savingCases.asReadonly();
  public readonly errorCases = this._errorCases.asReadonly();

  // Read-only signals - Cases list
  public readonly casesList = this._casesList.asReadonly();
  public readonly casesListTotalElements = this._casesListTotalElements.asReadonly();
  public readonly loadingCasesList = this._loadingCasesList.asReadonly();
  public readonly errorCasesList = this._errorCasesList.asReadonly();

  // Read-only signals - Status snapshots
  public readonly statusSnapshots = this._statusSnapshots.asReadonly();
  public readonly loadingStatusSnapshots = this._loadingStatusSnapshots.asReadonly();
  public readonly errorStatusSnapshots = this._errorStatusSnapshots.asReadonly();

  // Read-only signals - Priority snapshots
  public readonly prioritySnapshots = this._prioritySnapshots.asReadonly();
  public readonly loadingPrioritySnapshots = this._loadingPrioritySnapshots.asReadonly();
  public readonly errorPrioritySnapshots = this._errorPrioritySnapshots.asReadonly();

  // Read-only signals - Checklist steps
  public readonly checklistSteps = this._checklistSteps.asReadonly();
  public readonly loadingChecklistSteps = this._loadingChecklistSteps.asReadonly();
  public readonly updatingChecklistStep = this._updatingChecklistStep.asReadonly();
  public readonly errorChecklistSteps = this._errorChecklistSteps.asReadonly();

  // Read-only signals - Messages
  public readonly messages = this._messages.asReadonly();
  public readonly loadingMessages = this._loadingMessages.asReadonly();
  public readonly addingMessage = this._addingMessage.asReadonly();
  public readonly markingMessagesAsRead = this._markingMessagesAsRead.asReadonly();
  public readonly errorMessages = this._errorMessages.asReadonly();

  // Read-only signals - Work notes
  public readonly workNotes = this._workNotes.asReadonly();
  public readonly loadingWorkNotes = this._loadingWorkNotes.asReadonly();
  public readonly addingWorkNote = this._addingWorkNote.asReadonly();
  public readonly errorWorkNotes = this._errorWorkNotes.asReadonly();

  // Read-only signals - Attachments
  public readonly attachments = this._attachments.asReadonly();
  public readonly loadingAttachments = this._loadingAttachments.asReadonly();
  public readonly uploadingAttachment = this._uploadingAttachment.asReadonly();
  public readonly deletingAttachmentId = this._deletingAttachmentId.asReadonly();
  public readonly errorAttachments = this._errorAttachments.asReadonly();

  // Read-only signals - Linked cases
  public readonly linkedCases = this._linkedCases.asReadonly();
  public readonly loadingLinkedCases = this._loadingLinkedCases.asReadonly();
  public readonly linkingCase = this._linkingCase.asReadonly();
  public readonly unlinkingCase = this._unlinkingCase.asReadonly();
  public readonly errorLinkedCases = this._errorLinkedCases.asReadonly();

  // Read-only signals - Employees
  public readonly employees = this._employees.asReadonly();
  public readonly loadingEmployees = this._loadingEmployees.asReadonly();
  public readonly errorEmployees = this._errorEmployees.asReadonly();

  // Read-only signals - Closing case
  public readonly closingCase = this._closingCase.asReadonly();
  public readonly errorClosingCase = this._errorClosingCase.asReadonly();

  // Read-only signals - Reopening case
  public readonly reopeningCase = this._reopeningCase.asReadonly();
  public readonly errorReopeningCase = this._errorReopeningCase.asReadonly();

  // Read-only signals - Involved employees
  public readonly involvedEmployees = this._involvedEmployees.asReadonly();
  public readonly loadingInvolvedEmployees = this._loadingInvolvedEmployees.asReadonly();
  public readonly errorInvolvedEmployees = this._errorInvolvedEmployees.asReadonly();

  // Read-only signals - Parent/child cases
  public readonly parentChildCases = this._parentChildCases.asReadonly();
  public readonly loadingParentChildCases = this._loadingParentChildCases.asReadonly();
  public readonly errorParentChildCases = this._errorParentChildCases.asReadonly();

  // Computed signals
  public readonly casesCount = computed(() => this._cases().length);
  public readonly hasCases = computed(() => this._cases().length > 0);
  public readonly casesListCount = computed(() => this._casesList().length);
  public readonly hasStatusSnapshots = computed(() => this._statusSnapshots().length > 0);
  public readonly hasPrioritySnapshots = computed(() => this._prioritySnapshots().length > 0);
  public readonly hasChecklistSteps = computed(() => this._checklistSteps().length > 0);
  public readonly completedChecklistSteps = computed(() => this._checklistSteps().filter((s) => s.isCompleted).length);
  public readonly hasMessages = computed(() => this._messages().length > 0);
  public readonly hasWorkNotes = computed(() => this._workNotes().length > 0);
  public readonly hasAttachments = computed(() => this._attachments().length > 0);
  public readonly hasLinkedCases = computed(() => this._linkedCases().length > 0);
  public readonly hasEmployees = computed(() => this._employees().length > 0);

  /**
   * Sets the Cases.
   * @param Cases - Array of Case entities
   */
  public setCases(Cases: CaseEntity[]): void {
    this._cases.set(Cases);
  }

  /**
   * Sets the selected Case.
   * @param Case - Case entity or null
   */
  public setSelectedCase(Case: CaseEntity | null): void {
    this._selectedCase.set(Case);
  }

  /**
   * Sets the loading state for Cases.
   * @param loading - Loading state
   */
  public setLoadingCases(loading: boolean): void {
    this._loadingCases.set(loading);
  }

  /**
   * Sets the saving state for Cases.
   * @param saving - Saving state
   */
  public setSavingCases(saving: boolean): void {
    this._savingCases.set(saving);
  }

  /**
   * Sets an error message for Cases.
   * @param error - Error message
   */
  public setErrorCases(error: string | null): void {
    this._errorCases.set(error);
  }

  /**
   * Sets the cases list (summary data).
   * @param casesList - Array of case list item entities
   */
  public setCasesList(casesList: CaseListItemEntity[]): void {
    this._casesList.set(casesList);
  }

  /**
   * Appends cases to the existing cases list (for pagination).
   * @param casesList - Array of case list item entities to append
   */
  public appendCasesList(casesList: CaseListItemEntity[]): void {
    this._casesList.update((current) => [...current, ...casesList]);
  }

  /**
   * Sets the total elements count for cases list (for pagination).
   * @param total - Total number of elements
   */
  public setCasesListTotalElements(total: number): void {
    this._casesListTotalElements.set(total);
  }

  /**
   * Sets the loading state for cases list.
   * @param loading - Loading state
   */
  public setLoadingCasesList(loading: boolean): void {
    this._loadingCasesList.set(loading);
  }

  /**
   * Sets an error message for cases list.
   * @param error - Error message
   */
  public setErrorCasesList(error: string | null): void {
    this._errorCasesList.set(error);
  }

  /**
   * Sets the status snapshots.
   */
  public setStatusSnapshots(snapshots: CaseStatusSnapshotEntity[]): void {
    this._statusSnapshots.set(snapshots);
  }

  public setLoadingStatusSnapshots(loading: boolean): void {
    this._loadingStatusSnapshots.set(loading);
  }

  public setErrorStatusSnapshots(error: string | null): void {
    this._errorStatusSnapshots.set(error);
  }

  /**
   * Sets the priority snapshots.
   */
  public setPrioritySnapshots(snapshots: CasePrioritySnapshotEntity[]): void {
    this._prioritySnapshots.set(snapshots);
  }

  public setLoadingPrioritySnapshots(loading: boolean): void {
    this._loadingPrioritySnapshots.set(loading);
  }

  public setErrorPrioritySnapshots(error: string | null): void {
    this._errorPrioritySnapshots.set(error);
  }

  /**
   * Sets the checklist steps.
   */
  public setChecklistSteps(steps: ChecklistStepEntity[]): void {
    this._checklistSteps.set(steps);
  }

  public setLoadingChecklistSteps(loading: boolean): void {
    this._loadingChecklistSteps.set(loading);
  }

  public setUpdatingChecklistStep(updating: boolean): void {
    this._updatingChecklistStep.set(updating);
  }

  public setErrorChecklistSteps(error: string | null): void {
    this._errorChecklistSteps.set(error);
  }

  /**
   * Updates a specific checklist step in state.
   */
  public updateChecklistStepCompletion(stepId: string, isCompleted: boolean): void {
    this._checklistSteps.update((steps) =>
      steps.map((step) => (step.id === stepId ? { ...step, isCompleted } : step))
    );
  }

  /**
   * Sets the messages.
   */
  public setMessages(messages: CaseMessageEntity[]): void {
    this._messages.set(messages);
  }

  public setLoadingMessages(loading: boolean): void {
    this._loadingMessages.set(loading);
  }

  public setAddingMessage(adding: boolean): void {
    this._addingMessage.set(adding);
  }

  public setMarkingMessagesAsRead(marking: boolean): void {
    this._markingMessagesAsRead.set(marking);
  }

  public setErrorMessages(error: string | null): void {
    this._errorMessages.set(error);
  }

  /**
   * Adds a new message to the state.
   */
  public addMessage(message: CaseMessageEntity): void {
    this._messages.update((messages) => [...messages, message]);
  }

  /**
   * Sets the work notes.
   */
  public setWorkNotes(workNotes: WorkNoteEntity[]): void {
    this._workNotes.set(workNotes);
  }

  public setLoadingWorkNotes(loading: boolean): void {
    this._loadingWorkNotes.set(loading);
  }

  public setAddingWorkNote(adding: boolean): void {
    this._addingWorkNote.set(adding);
  }

  public setErrorWorkNotes(error: string | null): void {
    this._errorWorkNotes.set(error);
  }

  /**
   * Adds a new work note to the state.
   */
  public addWorkNote(workNote: WorkNoteEntity): void {
    this._workNotes.update((workNotes) => [...workNotes, workNote]);
  }

  /**
   * Sets the attachments.
   */
  public setAttachments(attachments: CaseAttachmentEntity[]): void {
    this._attachments.set(attachments);
  }

  public setLoadingAttachments(loading: boolean): void {
    this._loadingAttachments.set(loading);
  }

  public setErrorAttachments(error: string | null): void {
    this._errorAttachments.set(error);
  }

  /**
   * Sets the uploading attachment state.
   */
  public setUploadingAttachment(uploading: boolean): void {
    this._uploadingAttachment.set(uploading);
  }

  /**
   * Sets the ID of the attachment being deleted (null when not deleting).
   */
  public setDeletingAttachmentId(attachmentId: string | null): void {
    this._deletingAttachmentId.set(attachmentId);
  }

  /**
   * Adds a new attachment to the state.
   */
  public addAttachment(attachment: CaseAttachmentEntity): void {
    this._attachments.update((attachments) => [...attachments, attachment]);
  }

  /**
   * Removes an attachment from the state.
   */
  public removeAttachment(attachmentId: string): void {
    this._attachments.update((attachments) => attachments.filter((a) => a.id !== attachmentId));
  }

  /**
   * Sets the linked cases.
   */
  public setLinkedCases(linkedCases: LinkedCaseEntity[]): void {
    this._linkedCases.set(linkedCases);
  }

  public setLoadingLinkedCases(loading: boolean): void {
    this._loadingLinkedCases.set(loading);
  }

  public setLinkingCase(linking: boolean): void {
    this._linkingCase.set(linking);
  }

  public setUnlinkingCase(unlinking: boolean): void {
    this._unlinkingCase.set(unlinking);
  }

  public setErrorLinkedCases(error: string | null): void {
    this._errorLinkedCases.set(error);
  }

  /**
   * Adds a new linked case to the state.
   */
  public addLinkedCase(linkedCase: LinkedCaseEntity): void {
    this._linkedCases.update((linkedCases) => [...linkedCases, linkedCase]);
  }

  /**
   * Removes a linked case from the state.
   */
  public removeLinkedCase(linkedCaseId: string): void {
    this._linkedCases.update((linkedCases) => linkedCases.filter((lc) => lc.linkedCaseId !== linkedCaseId));
  }

  /**
   * Sets the employees.
   */
  public setEmployees(employees: EmployeeEntity[]): void {
    this._employees.set(employees);
  }

  public setLoadingEmployees(loading: boolean): void {
    this._loadingEmployees.set(loading);
  }

  public setErrorEmployees(error: string | null): void {
    this._errorEmployees.set(error);
  }

  /**
   * Sets the closing case state.
   * @param closing - Closing state
   */
  public setClosingCase(closing: boolean): void {
    this._closingCase.set(closing);
  }

  /**
   * Sets an error message for closing case.
   * @param error - Error message
   */
  public setErrorClosingCase(error: string | null): void {
    this._errorClosingCase.set(error);
  }

  /**
   * Sets the reopening case state.
   * @param reopening - Reopening state
   */
  public setReopeningCase(reopening: boolean): void {
    this._reopeningCase.set(reopening);
  }

  /**
   * Sets an error message for reopening case.
   * @param error - Error message
   */
  public setErrorReopeningCase(error: string | null): void {
    this._errorReopeningCase.set(error);
  }

  /**
   * Clears all Case state.
   */
  public clear(): void {
    this._cases.set([]);
    this._selectedCase.set(null);
    this._loadingCases.set(false);
    this._savingCases.set(false);
    this._errorCases.set(null);
    this._casesList.set([]);
    this._loadingCasesList.set(false);
    this._errorCasesList.set(null);
    this._statusSnapshots.set([]);
    this._loadingStatusSnapshots.set(false);
    this._errorStatusSnapshots.set(null);
    this._prioritySnapshots.set([]);
    this._loadingPrioritySnapshots.set(false);
    this._errorPrioritySnapshots.set(null);
    this._checklistSteps.set([]);
    this._loadingChecklistSteps.set(false);
    this._updatingChecklistStep.set(false);
    this._errorChecklistSteps.set(null);
    this._messages.set([]);
    this._loadingMessages.set(false);
    this._addingMessage.set(false);
    this._markingMessagesAsRead.set(false);
    this._errorMessages.set(null);
    this._workNotes.set([]);
    this._loadingWorkNotes.set(false);
    this._addingWorkNote.set(false);
    this._errorWorkNotes.set(null);
    this._attachments.set([]);
    this._loadingAttachments.set(false);
    this._uploadingAttachment.set(false);
    this._deletingAttachmentId.set(null);
    this._errorAttachments.set(null);
    this._linkedCases.set([]);
    this._loadingLinkedCases.set(false);
    this._linkingCase.set(false);
    this._unlinkingCase.set(false);
    this._errorLinkedCases.set(null);
    this._employees.set([]);
    this._loadingEmployees.set(false);
    this._errorEmployees.set(null);
    this._closingCase.set(false);
    this._errorClosingCase.set(null);
    this._reopeningCase.set(false);
    this._errorReopeningCase.set(null);
    this._involvedEmployees.set([]);
    this._loadingInvolvedEmployees.set(false);
    this._errorInvolvedEmployees.set(null);
    this._parentChildCases.set(null);
    this._loadingParentChildCases.set(false);
    this._errorParentChildCases.set(null);
  }

  /**
   * Sets the involved employees.
   */
  public setInvolvedEmployees(employees: EmployeeEntity[]): void {
    this._involvedEmployees.set(employees);
  }

  public setLoadingInvolvedEmployees(loading: boolean): void {
    this._loadingInvolvedEmployees.set(loading);
  }

  public setErrorInvolvedEmployees(error: string | null): void {
    this._errorInvolvedEmployees.set(error);
  }

  /**
   * Sets the parent/child cases data.
   */
  public setParentChildCases(data: ParentChildCasesEntity): void {
    this._parentChildCases.set(data);
  }

  public setLoadingParentChildCases(loading: boolean): void {
    this._loadingParentChildCases.set(loading);
  }

  public setErrorParentChildCases(error: string | null): void {
    this._errorParentChildCases.set(error);
  }
}
